import React from 'react';
import { Badge, type BadgeProps } from './Badge';
import { cn } from '@/lib/utils';

export type StatusTone = 'neutral' | 'info' | 'success' | 'warning' | 'critical' | 'caution';

const BADGE_VARIANT: Record<StatusTone, NonNullable<BadgeProps['variant']>> = {
  neutral: 'neutral',
  info: 'info',
  success: 'success',
  warning: 'warning',
  critical: 'critical',
  caution: 'caution',
};

const DOT_CLASS: Record<StatusTone, string> = {
  neutral: 'bg-status-neutral',
  info: 'bg-status-info',
  success: 'bg-status-success',
  warning: 'bg-status-warning',
  critical: 'bg-status-critical',
  caution: 'bg-status-caution',
};

export interface StatusBadgeProps extends Omit<BadgeProps, 'variant'> {
  tone?: StatusTone;
  showDot?: boolean;
}

/** Semantic state label. Use one tone cue per state and sentence-case copy. */
export function StatusBadge({ tone = 'neutral', showDot = false, className, children, ...props }: StatusBadgeProps) {
  return (
    <Badge variant={BADGE_VARIANT[tone]} className={cn(className)} {...props}>
      {showDot && <span className={cn('h-1.5 w-1.5 shrink-0 rounded-full', DOT_CLASS[tone])} aria-hidden="true" />}
      {children}
    </Badge>
  );
}

export interface MetadataTagProps extends React.HTMLAttributes<HTMLSpanElement> {
  selected?: boolean;
}

/** Neutral, non-semantic metadata such as role, category, source, or environment. */
export function MetadataTag({ selected = false, className, ...props }: MetadataTagProps) {
  return (
    <span
      className={cn('ax-chip min-h-[26px]', selected && 'ax-chip--selected', className)}
      {...props}
    />
  );
}

export interface CounterBadgeProps extends React.HTMLAttributes<HTMLSpanElement> {
  critical?: boolean;
  value?: React.ReactNode;
}

/** Compact count with tabular numerals; critical is reserved for urgent unread work. */
export function CounterBadge({ critical = false, value, className, children, ...props }: CounterBadgeProps) {
  return (
    <span className={cn('ax-counter', critical && 'ax-counter--critical', className)} {...props}>
      {value ?? children}
    </span>
  );
}

export interface SignalDotProps extends React.HTMLAttributes<HTMLSpanElement> {
  tone?: StatusTone;
  label?: string;
}

/** Static status signal. Animated pulses are reserved for genuinely live activity. */
export function SignalDot({ tone = 'neutral', label, className, ...props }: SignalDotProps) {
  return (
    <span
      className={cn('inline-block h-1.5 w-1.5 shrink-0 rounded-full', DOT_CLASS[tone], className)}
      role={label ? 'img' : undefined}
      aria-label={label}
      aria-hidden={label ? undefined : true}
      {...props}
    />
  );
}
